import type { ReactNode } from 'react'
import type { PosShift } from '@core/db/models'
import type { KktStatus } from '@core/services/fiscal/FiscalService'
import type { ScaleStatus } from '@core/services/scales/ScalesService'
import type { CurrencyCode } from '@core/utils/currency'
import { formatCurrency } from '@core/utils/currency'
import { pluralRuCount } from '@core/utils/plural'
import { isNativeKkt } from '@core/native/kkt'
import { badge, btn, btnBase, btnSize, chip, cx } from '@ui/classes'
import { Clock, Close, Info, Printer, Scales } from '@ui/icons'

export interface PosShiftPanelProps {
  shift: PosShift
  kktStatus: KktStatus | null
  kktBadgeLabel: string
  kktBadgeOk: boolean
  scaleStatus: ScaleStatus
  canManageShifts: boolean
  isOpenerDevice: boolean
  returnMode: boolean
  paying: boolean
  currency: CurrencyCode
  onMore: () => void
  onExitReturn: () => void
  onCloseShift: () => void
  onRetryKktSync: () => void
  /** Поле поиска товара — справа в строке оборудования. */
  search?: ReactNode
}

/**
 * Компактная панель кассы: смена, ККТ и весы в одной карточке.
 *
 * Две строки: состояние смены с действиями и строка оборудования. Метрики
 * идут инлайн через разделители, чтобы панель занимала минимум высоты и
 * не отнимала место у плиток каталога.
 */
export function PosShiftPanel({
  shift,
  kktStatus,
  kktBadgeLabel,
  kktBadgeOk,
  scaleStatus,
  canManageShifts,
  isOpenerDevice,
  returnMode,
  paying,
  currency,
  onMore,
  onExitReturn,
  onCloseShift,
  onRetryKktSync,
  search
}: PosShiftPanelProps) {
  const staleWarn = Date.now() - shift.openedAt > 23 * 60 * 60 * 1000
  const kktDocsIncomplete =
    shift.kktZComplete === false || (isNativeKkt() && shift.kktZComplete !== true)
  const openedAt = new Date(shift.openedAt).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit'
  })
  const returns = shift.returnsCount ?? 0

  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      {/* Смена: одна строка с метриками и действиями. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-900">
        <span className="whitespace-nowrap font-bold">Смена №{shift.shiftNumber}</span>
        <span className="max-w-[12rem] truncate text-tiny opacity-80">
          {shift.cashierName}
        </span>
        <span className="text-tiny tabular-nums opacity-80">
          Время открытия: {openedAt} · Продаж: {shift.salesCount} · Выручка:{' '}
          {formatCurrency(shift.totalSales, currency)}
          {returns > 0
            ? ` · Возвратов: ${returns} на ${formatCurrency(shift.returnsTotal ?? 0, currency)}`
            : ''}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            className={cx(
              btnBase,
              btnSize.sm,
              'border-emerald-200 bg-white text-emerald-900 hover:bg-emerald-100'
            )}
            onClick={onMore}
            aria-haspopup="dialog"
          >
            Ещё…
          </button>
          {returnMode && (
            <button
              className={cx(
                btnBase,
                btnSize.sm,
                'border-danger bg-danger text-white hover:opacity-90 disabled:opacity-50'
              )}
              onClick={onExitReturn}
              disabled={paying}
              title="Выйти из режима возврата"
            >
              <Close size={16} /> Выйти
            </button>
          )}
          {canManageShifts ? (
            <button
              className={cx(
                btnBase,
                btnSize.sm,
                'border-emerald-200 bg-white text-emerald-900 hover:bg-emerald-100 disabled:opacity-50'
              )}
              disabled={!isOpenerDevice}
              title={
                isOpenerDevice
                  ? 'Закрыть смену'
                  : 'Смену закрывают на кассе-открывателе (или из кабинета директора)'
              }
              onClick={onCloseShift}
            >
              Закрыть смену
            </button>
          ) : (
            <span className="text-tiny text-warnFg">нужны права администратора</span>
          )}
        </div>
      </div>

      {/* ККТ и весы: одна строка. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-3 py-1 text-tiny">
        <span className="flex items-center gap-1">
          <Printer size={16} className="text-muted" />
          <span className="text-muted">ККТ:</span>
          <span
            className={cx(
              chip,
              !kktStatus ? badge.neutral : kktBadgeOk ? badge.ok : badge.danger
            )}
            title="Состояние фискального регистратора (ККТ)"
          >
            {kktBadgeLabel}
            {kktStatus?.lastDocNumber ? ` · ФД ${kktStatus.lastDocNumber}` : ''}
          </span>
        </span>
        <span className="flex items-center gap-1">
          <Scales size={16} className="text-muted" />
          <span className="text-muted">Весы:</span>
          <span
            className={cx(
              chip,
              scaleStatus.connected
                ? badge.ok
                : scaleStatus.protocol
                  ? badge.danger
                  : badge.neutral
            )}
            title="Весы: состояние подключения"
          >
            {scaleStatus.connected
              ? scaleStatus.port ?? scaleStatus.protocol ?? 'подключены'
              : scaleStatus.protocol
                ? 'нет связи'
                : 'не настроены'}
          </span>
        </span>
        {search && <div className="ml-auto w-full sm:w-[260px]">{search}</div>}
      </div>

      {/* Предупреждения — только при наличии, компактными строками. */}
      {(!isOpenerDevice ||
        staleWarn ||
        (shift.kktExternalReceipts ?? 0) > 0 ||
        kktDocsIncomplete ||
        shift.kktSyncError) && (
        <div className="flex flex-col gap-0.5 border-t border-line px-3 py-1 text-[10px] leading-4">
          {!isOpenerDevice && (
            <span className="inline-flex items-start gap-1 text-warnFg">
              <Info size={16} className="mt-0.5 shrink-0" /> Смену открыто на другой кассе
              зоны — закрытие только на кассе-открывателе.
            </span>
          )}
          {staleWarn && (
            <span className="inline-flex items-start gap-1 text-amber-700">
              <Clock size={16} className="mt-0.5 shrink-0" /> Смена открыта более 23 ч —
              закройте её, иначе она закроется по таймауту 24 ч.
            </span>
          )}
          {(shift.kktExternalReceipts ?? 0) > 0 && (
            <span className="inline-flex items-start gap-1 text-warnFg">
              <Info size={16} className="mt-0.5 shrink-0" />
              В смене ККТ есть{' '}
              {pluralRuCount(shift.kktExternalReceipts ?? 0, [
                'документ',
                'документа',
                'документов'
              ])}{' '}
              другого ПО — Z-отчёт будет неполным.
            </span>
          )}
          {kktDocsIncomplete && (shift.kktExternalReceipts ?? 0) === 0 && (
            <span className="inline-flex items-start gap-1 text-amber-700">
              <Info size={16} className="mt-0.5 shrink-0" />
              Документы смены ККТ прочитаны не полностью — Z-отчёт будет неполным.
            </span>
          )}
          {shift.kktSyncError && (
            <span className="inline-flex items-start gap-1 text-warnFg">
              <Info size={16} className="mt-0.5 shrink-0" />
              Сверка ККТ: {shift.kktSyncError}
            </span>
          )}
          {kktDocsIncomplete && (
            <button
              type="button"
              className={cx(btnBase, btn.secondary, btnSize.sm, 'mt-0.5 self-start')}
              onClick={onRetryKktSync}
            >
              Повторить сверку
            </button>
          )}
        </div>
      )}
    </div>
  )
}
